import React, { Ref, ChangeEvent, useEffect, useRef } from 'react';
import { Icon } from '@highlight-ui/icon';
import { useForkRef, useId } from '@highlight-ui/utils-hooks';

import styles from './DefaultSearchInput.module.scss';
import { SearchInputProps } from './SearchInput';

type SearchInputRef = Ref<HTMLInputElement>;

const SearchInputWithRef = (
  {
    placeholder = '',
    value,
    onTextChange,
    onKeyDown,
    searchAutofocus = true,
  }: SearchInputProps,
  ref: SearchInputRef,
) => {
  const id = useId('component-');
  const inputRef = useRef<HTMLInputElement>(null);

  const finalInputRef = useForkRef<HTMLDivElement>([ref, inputRef]);

  useEffect(() => {
    if (inputRef.current && searchAutofocus) {
      inputRef.current.focus();
    }
  }, [ref, searchAutofocus]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onTextChange(event.currentTarget.value);

  return (
    <div className={styles.wrapper} role="search">
      <label htmlFor={id}>
        <Icon
          className={styles.icon}
          color="icon-subdued"
          name="search"
          size={12}
        />
        <input
          defaultValue={value}
          id={id}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          ref={finalInputRef}
          type="search"
        />
      </label>
    </div>
  );
};

SearchInputWithRef.displayName = 'SearchInput';

const DefaultSearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  SearchInputWithRef,
);

export default DefaultSearchInput;
